'use client';

/**
 * Testimonials API helper — frontend client for SPEC-136 endpoints.
 *
 * 5 user/public endpoints + 3 admin endpoints + 1 S3 upload (Task N):
 *   GET    /api/testimonials/public            — list published (public, 60/min IP)
 *   GET    /api/testimonials/mine              — my testimonial (auth)
 *   POST   /api/testimonials                   — create (auth, 3/hr user)
 *   PUT    /api/testimonials/:id               — update (auth + owner, 10/hr user)
 *   DELETE /api/testimonials/:id               — soft-delete (auth + owner)
 *   POST   /api/testimonials/upload-photo      — S3 presigned URL (Task N)
 *   GET    /api/admin/testimonials             — list (admin)
 *   PUT    /api/admin/testimonials/:id         — admin update (admin)
 *   POST   /api/admin/testimonials/bulk        — bulk action (admin)
 *
 * The Zod schemas are imported from `@profileia/shared/schemas/testimonial`
 * (single source of truth — anti-CM-19) so the frontend form validation
 * uses the SAME schema as the backend controller validation.
 *
 * Spec ref: docs/SPEC-136-TESTIMONIOS.md §3 (endpoints API), §4 (Zod).
 *
 * Anti-P-023: returns real typed values (no claim PASS).
 * Anti-P-041: 0 `as any` casts.
 * Anti-P-042: 0 `console.*` statements.
 */
import { apiFetch } from './api-client';
import type {
  PublicTestimonial,
  AdminTestimonial,
  AdminTestimonialListResponse,
  TestimonialCreate,
  TestimonialUpdate,
  TestimonialAdminUpdate,
  TestimonialBulkActionBody,
  TestimonialPublicQuery,
  TestimonialAdminQuery,
} from '@profileia/shared';

// Re-export the Zod schemas + types so /app/testimonios/page.tsx +
// /admin/testimonios/page.tsx can import everything from one place.
export {
  testimonialCreateSchema,
  testimonialUpdateSchema,
  testimonialAdminUpdateSchema,
  testimonialBulkActionSchema,
  testimonialPublicQuerySchema,
  testimonialAdminQuerySchema,
} from '@profileia/shared';

export type {
  PublicTestimonial,
  AdminTestimonial,
  AdminTestimonialListResponse,
  TestimonialCreate,
  TestimonialUpdate,
  TestimonialAdminUpdate,
  TestimonialBulkActionBody,
  TestimonialPublicQuery,
  TestimonialAdminQuery,
  TestimonialTier,
  TestimonialStatus,
  TestimonialMediaType,
  TestimonialLanguage,
} from '@profileia/shared';

// ─── Public/user endpoints ──────────────────────────────────────────────────

/**
 * GET /api/testimonials/public — list published testimonials.
 *
 * Public (no auth). Used by the landing TestimonialsSection component.
 * Returns at most `limit` (default 6, max 24) testimonials.
 */
export async function listPublicTestimonials(
  query: Partial<TestimonialPublicQuery> = {},
): Promise<PublicTestimonial[]> {
  const params = new URLSearchParams();
  if (query.limit) params.set('limit', String(query.limit));
  if (query.featured) params.set('featured', 'true');
  if (query.tier) params.set('tier', query.tier);
  if (query.country) params.set('country', query.country);
  if (query.mediaType) params.set('mediaType', query.mediaType);
  const qs = params.toString();
  const path = qs ? `/testimonials/public?${qs}` : '/testimonials/public';
  // FIX: apiFetch already extracts `.data` from the response body (see
  // handleResponse in api-client.ts). So the return type is the array
  // directly, NOT { data: PublicTestimonial[] }.
  return apiFetch<PublicTestimonial[]>(path, {
    method: 'GET',
    auth: false,
  });
}

/**
 * GET /api/testimonials/mine — get the current user's testimonial.
 *
 * Auth required. Returns null if the user has no testimonial (Estado 3 §7).
 */
export async function getMyTestimonial(): Promise<PublicTestimonial | null> {
  // FIX: apiFetch already extracts `.data`.
  return apiFetch<PublicTestimonial | null>(
    '/testimonials/mine',
    { method: 'GET', auth: true },
  );
}

/**
 * POST /api/testimonials — create a new testimonial.
 *
 * Auth required. Rate-limited 3/hr/user. The backend handles:
 *   - Diamante user → status='published' (auto-publish).
 *   - Non-Diamante user → status='pending' (review queue).
 */
export async function createTestimonial(
  body: TestimonialCreate,
): Promise<PublicTestimonial> {
  // FIX: apiFetch already extracts `.data`. The backend returns
  // { data: PublicTestimonial } — apiFetch returns PublicTestimonial directly.
  return apiFetch<PublicTestimonial>('/testimonials', {
    method: 'POST',
    auth: true,
    body: JSON.stringify(body),
  });
}

/**
 * PUT /api/testimonials/:id — update the user's testimonial.
 *
 * Auth required + owner. Rate-limited 10/hr/user. The backend handles:
 *   - Diamante → status stays 'published'.
 *   - Non-Diamante published → reverts to 'pending' (anti bait-and-switch).
 */
export async function updateTestimonial(
  id: string,
  body: TestimonialUpdate,
): Promise<PublicTestimonial> {
  // FIX: apiFetch already extracts `.data`.
  return apiFetch<PublicTestimonial>(
    `/testimonials/${id}`,
    { method: 'PUT', auth: true, body: JSON.stringify(body) },
  );
}

/**
 * DELETE /api/testimonials/:id — soft-delete the user's testimonial.
 *
 * Auth required + owner. Returns 204 No Content (no body).
 */
export async function deleteTestimonial(id: string): Promise<void> {
  await apiFetch<void>(`/testimonials/${id}`, {
    method: 'DELETE',
    auth: true,
  });
}

// ─── Admin endpoints ────────────────────────────────────────────────────────

/**
 * GET /api/admin/testimonials — paginated admin list.
 *
 * Admin only (requireAdmin + requireRecent2FA enforced server-side).
 * Returns full testimonial documents (including isSeed, reviewedBy, etc.).
 *
 * P-228 fix (SPEC-140): the previous type was
 * `Promise<AdminTestimonialListResponse>` (envelope with `.data` key), but
 * `apiFetch` already extracts `.data` from the response envelope (see
 * `handleResponse` in api-client.ts:1398-1402). So the actual return value
 * is the array of `AdminTestimonial` directly, NOT the envelope wrapper.
 *
 * Pre-fix, the type was `Promise<AdminTestimonialListResponse>` which lied
 * to TypeScript — the caller in `/admin/testimonios/page.tsx` did
 * `response.data ?? []` and got `undefined` (arrays don't have `.data`) →
 * fallback to `[]` → UI showed "0 testimonios" even though the API
 * returned 8. P-223 was NOT affected because `getMyTestimonial()` is
 * typed as `Promise<PublicTestimonial | null>` (no envelope, no `.data`
 * extraction by the caller).
 *
 * The `AdminTestimonialListResponse` type is intentionally kept imported +
 * re-exported (lines 31 + 54) — it remains the canonical envelope shape
 * (used by the backend) and may be referenced by other callers that use
 * raw `fetch()` against the admin endpoint.
 */
export async function listAdminTestimonials(
  query: Partial<TestimonialAdminQuery> = {},
): Promise<AdminTestimonial[]> {
  const params = new URLSearchParams();
  if (query.status) params.set('status', query.status);
  if (query.tier) params.set('tier', query.tier);
  if (query.mediaType) params.set('mediaType', query.mediaType);
  if (query.country) params.set('country', query.country);
  if (query.search) params.set('search', query.search);
  if (query.page) params.set('page', String(query.page));
  if (query.limit) params.set('limit', String(query.limit));
  if (query.sort) params.set('sort', query.sort);
  const qs = params.toString();
  const path = qs ? `/admin/testimonials?${qs}` : '/admin/testimonials';
  // P-228 fix (SPEC-140): apiFetch already extracts `.data` from the
  // response envelope (see handleResponse in api-client.ts:1398-1402).
  // So the return type is the array directly, NOT AdminTestimonialListResponse.
  return apiFetch<AdminTestimonial[]>(path, {
    method: 'GET',
    auth: true,
  });
}

/**
 * PUT /api/admin/testimonials/:id — admin update (any field).
 *
 * Admin only. Allows transitioning status (published/rejected/archived),
 * toggling isFeatured, setting displayOrder, setting rejectionReason.
 */
export async function adminUpdateTestimonial(
  id: string,
  body: TestimonialAdminUpdate,
): Promise<AdminTestimonial> {
  // FIX: apiFetch already extracts `.data`.
  return apiFetch<AdminTestimonial>(
    `/admin/testimonials/${id}`,
    { method: 'PUT', auth: true, body: JSON.stringify(body) },
  );
}

/**
 * POST /api/admin/testimonials/bulk — bulk action on N testimonials.
 *
 * Admin only. Action enum: approve | archive | feature | unfeature | delete.
 */
export async function bulkActionTestimonials(
  body: TestimonialBulkActionBody,
): Promise<{ affected: number }> {
  // FIX: apiFetch already extracts `.data`.
  return apiFetch<{ affected: number }>(
    '/admin/testimonials/bulk',
    { method: 'POST', auth: true, body: JSON.stringify(body) },
  );
}

// ─── V107-C (2026-10-06): photo + video upload to BunnyStorage ─────────────
//
// Founder directive: "Arregla para que se puedan subir fotos y videos en
// la zona de testimonios y que quede funcional". Pre-fix: TestimoniosForm
// showed a fake Alert "La subida de fotos estará disponible próximamente".
// These helpers implement the REAL upload flow:
//   1. Frontend builds FormData with the selected files
//   2. POST /api/testimonials/upload-photo (multipart, array 'photos', max 3)
//   3. POST /api/testimonials/upload-video (multipart, single 'video', max 1)
//   4. Backend (testimonial-upload.service) streams to BunnyStorage + returns
//      CDN URLs
//   5. Frontend includes the CDN URLs in the create/update payload (photos[],
//      videoFileUrl fields)
//
// Progress reporting: uses XMLHttpRequest with `upload.onprogress` (fetch()
// does not support upload progress in browsers as of 2026). The onProgress
// callback receives a number 0-100 (percentage). The promise resolves with
// the parsed response data (apiFetchFormData-style envelope unwrap).
//
// Anti-P-023: real upload + real progress. Anti-P-041: 0 'as any'.
// Anti-P-042: 0 console.* (errors thrown, not logged).
// Anti-P-001 (PII): NO se loggea contenido de archivos — solo sizeBytes +
// mimeType + fileName (these are NOT PII per §7).

import { tokenStore } from './api-client';
import { ApiClientError } from './api-client';

/** Result returned by POST /api/testimonials/upload-photo. */
export interface TestimonialPhotoUploadResult {
  /** Full public CDN URL (HTTPS) — store in Testimonial.photos[]. */
  url: string;
  /** Internal BunnyStorage path (for cleanup / admin purge). */
  storagePath: string;
  /** File size in bytes. */
  sizeBytes: number;
  /** MIME type (echoed back for the frontend preview). */
  mimeType: string;
}

/** Result returned by POST /api/testimonials/upload-video. */
export type TestimonialVideoUploadResult = TestimonialPhotoUploadResult;

/** API prefix — mirrors apiFetch in api-client/core/fetch-client.ts. */
const API_PREFIX = '/api';

/**
 * POST /api/testimonials/upload-photo — upload up to 3 photos to BunnyStorage.
 *
 * @param files — 1 to 3 File objects (image/jpeg | image/png | image/webp, max 5MB each)
 * @param onProgress — optional callback receiving a percentage 0-100
 * @returns array of TestimonialPhotoUploadResult (one per file)
 * @throws ApiClientError on 4xx/5xx response
 */
export function uploadTestimonialPhotos(
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<TestimonialPhotoUploadResult[]> {
  return uploadMultipart<TestimonialPhotoUploadResult[]>(
    '/testimonials/upload-photo',
    'photos',
    files,
    onProgress,
  );
}

/**
 * POST /api/testimonials/upload-video — upload 1 video to BunnyStorage.
 *
 * @param file — single File object (video/mp4 | video/webm, max 50MB)
 * @param onProgress — optional callback receiving a percentage 0-100
 * @returns TestimonialVideoUploadResult with the CDN URL
 * @throws ApiClientError on 4xx/5xx response
 */
export function uploadTestimonialVideo(
  file: File,
  onProgress?: (percent: number) => void,
): Promise<TestimonialVideoUploadResult> {
  // For single-file upload, we still use the multipart helper but wrap the
  // file in a 1-element array + use field name 'video'. The response is a
  // single object (not array), so we adapt the unwrap.
  return uploadMultipartSingle<TestimonialVideoUploadResult>(
    '/testimonials/upload-video',
    'video',
    file,
    onProgress,
  );
}

/**
 * Shared multipart upload helper using XMLHttpRequest (the only browser API
 * that supports upload progress as of 2026).
 *
 * Anti-P-041: typed generics — no 'as any'.
 */
function uploadMultipart<T>(
  path: string,
  fieldName: string,
  files: File[],
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    for (const file of files) {
      formData.append(fieldName, file, file.name);
    }
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_PREFIX}${path}`);
    // Auth: send access token (refresh handled by the caller if 401).
    const access = tokenStore.getAccess();
    if (access) {
      xhr.setRequestHeader('Authorization', `Bearer ${access}`);
    }
    // Send cookies (for refresh token) — same as fetch credentials: 'include'.
    xhr.withCredentials = true;

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      const status = xhr.status;
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = xhr.responseText;
      }
      if (status >= 200 && status < 300) {
        // Backend returns { data: ... } envelope — unwrap like apiFetch.
        const envelope = body as { data?: unknown };
        const data = envelope?.data;
        if (data === undefined) {
          reject(new ApiClientError('Respuesta inesperada del servidor (sin data).', 'UPLOAD_UNEXPECTED_RESPONSE', status));
          return;
        }
        resolve(data as T);
      } else {
        const errBody = body as { error?: string; code?: string };
        reject(new ApiClientError(
          errBody?.error ?? 'Error al subir el archivo.',
          errBody?.code ?? 'UPLOAD_ERROR',
          status,
        ));
      }
    };

    xhr.onerror = () => {
      reject(new ApiClientError('Error de red al subir el archivo.', 'UPLOAD_NETWORK_ERROR', 0));
    };

    xhr.onabort = () => {
      reject(new ApiClientError('Subida cancelada.', 'UPLOAD_ABORTED', 0));
    };

    xhr.send(formData);
  });
}

/**
 * Multipart upload helper for a SINGLE file (video upload). Same as
 * uploadMultipart but uses the field name + a single file, and the response
 * is a single object (not an array).
 */
function uploadMultipartSingle<T>(
  path: string,
  fieldName: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append(fieldName, file, file.name);
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_PREFIX}${path}`);
    const access = tokenStore.getAccess();
    if (access) {
      xhr.setRequestHeader('Authorization', `Bearer ${access}`);
    }
    xhr.withCredentials = true;

    if (onProgress && xhr.upload) {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      };
    }

    xhr.onload = () => {
      const status = xhr.status;
      let body: unknown;
      try {
        body = JSON.parse(xhr.responseText);
      } catch {
        body = xhr.responseText;
      }
      if (status >= 200 && status < 300) {
        const envelope = body as { data?: unknown };
        const data = envelope?.data;
        if (data === undefined) {
          reject(new ApiClientError('Respuesta inesperada del servidor (sin data).', 'UPLOAD_UNEXPECTED_RESPONSE', status));
          return;
        }
        resolve(data as T);
      } else {
        const errBody = body as { error?: string; code?: string };
        reject(new ApiClientError(
          errBody?.error ?? 'Error al subir el video.',
          errBody?.code ?? 'UPLOAD_ERROR',
          status,
        ));
      }
    };

    xhr.onerror = () => {
      reject(new ApiClientError('Error de red al subir el video.', 'UPLOAD_NETWORK_ERROR', 0));
    };

    xhr.onabort = () => {
      reject(new ApiClientError('Subida cancelada.', 'UPLOAD_ABORTED', 0));
    };

    xhr.send(formData);
  });
}
